import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export function ChiudiUtilizzoDialog({ riga, onClose }) {
  const qc = useQueryClient();
  const [cid, setCid] = useState(riga?.aperti?.length === 1 ? riga.aperti[0].commessa_id : "");
  const [usata, setUsata] = useState("");
  const aperta = riga?.aperti?.find((a) => a.commessa_id === cid);
  const residua = aperta ? Math.max(0, aperta.quantita - (Number(usata) || 0)) : 0;
  const mut = useMutation({
    mutationFn: async () => (await api.post("/magazzino/chiusure", { articolo_id: riga.articolo_id, variante: riga.variante || "", commessa_id: cid, quantita_usata: Number(usata) })).data,
    onSuccess: (r) => {
      toast.success(r.quantita_residua > 0 ? `Utilizzo chiuso: ${num(r.quantita_residua, 2)} ${riga.um} tornati in giacenza` : "Utilizzo chiuso: tutta la quantità è stata usata");
      ["mag-giacenze", "mag-carichi", "commessa", "materiali", "commesse"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      onClose();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  return (
    <Dialog open={!!riga} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="chiudi-utilizzo-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Chiudi utilizzo su commessa</DialogTitle>
          <DialogDescription className="truncate" title={`${riga?.codice} · ${riga?.descrizione}${riga?.variante ? ` - ${riga.variante}` : ""}`}>
            <span className="font-mono text-slate-700">{riga?.codice}</span> · {riga?.descrizione}{riga?.variante ? ` - ${riga.variante}` : ""}
          </DialogDescription>
          <p className="text-sm text-slate-500">Indica quanto è stato usato: il residuo torna in giacenza.</p>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Commessa</Label>
            <Select value={cid} onValueChange={setCid}><SelectTrigger data-testid="chiudi-commessa"><SelectValue placeholder="Seleziona commessa" /></SelectTrigger>
              <SelectContent>{(riga?.aperti || []).map((a) => <SelectItem key={a.commessa_id} value={a.commessa_id}>{a.codice} — {a.descrizione} ({num(a.quantita, 2)} {riga.um} assegnati)</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Quantità effettivamente usata ({riga?.um})</Label>
            <Input type="number" min="0" step="any" max={aperta?.quantita} value={usata} onChange={(e) => setUsata(e.target.value)} data-testid="chiudi-usata" /></div>
          {aperta && <div className="rounded-lg bg-slate-50 border px-3 py-2 text-sm" data-testid="chiudi-residua">Assegnati: <b className="font-mono">{num(aperta.quantita, 2)}</b> · Residuo che torna in magazzino: <b className="font-mono text-primary">{num(residua, 2)} {riga.um}</b></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => (cid && usata !== "" ? mut.mutate() : toast.error("Seleziona la commessa e indica la quantità usata"))} disabled={mut.isPending} data-testid="chiudi-salva">Chiudi utilizzo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
